import { useEffect, useState } from "react";
import type { PaletteMode } from "@mui/material";
import type { View } from "./types";
import Layout from "./components/Layout";
import AuthGuard from "./AuthGuard";
import TenantGuard from "./TenantGuard";
import Dashboard from "./views/Dashboard";
import Chargers from "./views/Chargers";
import ChargerDetail from "./views/ChargerDetail";
import Maintenance from "./views/Maintenance";
import Onboarding from "./views/Onboarding";
import Transactions from "./views/Transactions";
import Drivers from "./views/Drivers";
import DriverDetail from "./views/DriverDetail";
import SessionDetail from "./views/SessionDetail";
import DriverGroups from "./views/DriverGroups";
import ChargerGroups from "./views/ChargerGroups";
import Vendors from "./views/Vendors";
import ChargerModels from "./views/ChargerModels";
import ConfigTemplates from "./views/ConfigTemplates";
import Locations from "./views/Locations";
import LocationDetail from "./views/LocationDetail";
import Tariffs from "./views/Tariffs";
import Plans from "./views/Plans";
import PlanDetail from "./views/PlanDetail";
import Contracts from "./views/Contracts";
import Roaming from "./views/Roaming";
import Reports from "./views/Reports";
import ApiKeys from "./views/ApiKeys";
import Team from "./views/Team";

const listViews: View[] = [
  "dashboard", "chargers", "maintenance", "onboarding", "transactions", "drivers",
  "driver-groups", "charger-groups", "vendors", "charger-models", "locations", "tariffs", "plans", "contracts", "roaming", "reports", "api-keys", "team",
  "config-templates",
];

interface Route {
  view: View;
  id?: number;
}

interface Props {
  themeMode: PaletteMode;
  onToggleThemeMode: () => void;
}

function parseHash(): Route {
  const raw = window.location.hash.replace(/^#/, "");
  const slash = raw.indexOf("/");
  if (slash !== -1) {
    const viewPart = raw.slice(0, slash) as View;
    const idPart = parseInt(raw.slice(slash + 1), 10);
    if (!isNaN(idPart)) return { view: viewPart, id: idPart };
  }
  const view = raw as View;
  return { view: listViews.includes(view) ? view : "dashboard" };
}

export default function App({ themeMode, onToggleThemeMode }: Props) {
  const [route, setRoute] = useState<Route>(parseHash);

  useEffect(() => {
    const onHash = () => setRoute(parseHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const navigate = (v: View, id?: number) => {
    window.location.hash = id != null ? `${v}/${id}` : v;
    setRoute({ view: v, id });
  };

  return (
    <AuthGuard>
      <TenantGuard>
        <Layout
          view={route.view}
          onNavigate={navigate}
          themeMode={themeMode}
          onToggleThemeMode={onToggleThemeMode}
        >
          {route.view === "dashboard" && <Dashboard />}
          {route.view === "chargers" && <Chargers onOpenDetail={(id) => navigate("charger", id)} />}
          {route.view === "charger" && route.id != null && (
            <ChargerDetail
              id={route.id}
              onBack={() => navigate("chargers")}
              onOpenSession={(id) => navigate("session", id)}
            />
          )}
          {route.view === "maintenance" && <Maintenance />}
          {route.view === "onboarding" && <Onboarding />}
          {route.view === "transactions" && <Transactions />}
          {route.view === "drivers" && <Drivers onOpenDetail={(id) => navigate("driver", id)} />}
          {route.view === "driver" && route.id != null && (
            <DriverDetail
              id={route.id}
              onBack={() => navigate("drivers")}
              onOpenSession={(id) => navigate("session", id)}
            />
          )}
          {route.view === "session" && route.id != null && (
            <SessionDetail id={route.id} onBack={() => window.history.back()} />
          )}
          {route.view === "driver-groups" && <DriverGroups />}
          {route.view === "charger-groups" && <ChargerGroups />}
          {route.view === "vendors" && <Vendors />}
          {route.view === "charger-models" && <ChargerModels />}
          {route.view === "config-templates" && <ConfigTemplates />}
          {route.view === "locations" && <Locations onOpenDetail={(id) => navigate("location", id)} />}
          {route.view === "location" && route.id != null && (
            <LocationDetail id={route.id} onBack={() => navigate("locations")} />
          )}
          {route.view === "tariffs" && <Tariffs />}
          {route.view === "plans" && <Plans onOpenDetail={(id) => navigate("plan", id)} />}
          {route.view === "plan" && route.id != null && (
            <PlanDetail id={route.id} onBack={() => navigate("plans")} />
          )}
          {route.view === "contracts" && <Contracts />}
          {route.view === "roaming" && <Roaming />}
          {route.view === "reports" && <Reports />}
          {route.view === "api-keys" && <ApiKeys />}
          {route.view === "team" && <Team />}
        </Layout>
      </TenantGuard>
    </AuthGuard>
  );
}
